import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

import { api } from "../../../api";
import { extractCenterId } from "../../../utils/center";
import "../css/SimulationPage.css";

/*
 * Vue « historique des coûts » : toutes les simulations du centre en ordre chronologique (jour simulé),
 * avec une courbe par colonne du comparatif — réel payé / réel recalculé par la lib / simulé. Permet de
 * voir d'un coup d'œil l'écart lib vs réel au fil des journées rejouées.
 */

const SERIES = [
  { key: "paid", label: "Réel payé", color: "#2c3e50", pick: (s) => s.real && s.real.cost_eur },
  { key: "lib", label: "Réel (lib)", color: "#b9770e", pick: (s) => s.real_lib && s.real_lib.cost_eur },
  { key: "sim", label: "Simulé", color: "#2c7a4b", pick: (s) => s.sim && s.sim.cost_eur },
];

const MARGIN = { top: 16, right: 24, bottom: 46, left: 64 };
const POINT_SPACING = 72; // px entre deux simulations sur l'axe X
const MIN_HEIGHT = 320;
// Pas de la grille : 100 € tant que les lignes restent lisibles, sinon on desserre.
const GRID_STEPS = [100, 200, 500, 1000, 2000];
const GRID_MIN_GAP_PX = 12;
const TIP = { w: 168, h: 74, gap: 12 };

const fmtEur = (v) => (v == null ? "—" : `${Math.round(v).toLocaleString("fr-FR")} €`);
const fmtDay = (iso) => {
  const parts = (iso || "").split("-");
  return parts[2] && parts[1] ? `${parts[2]}/${parts[1]}` : iso;
};

// Hauteur disponible sous le graphique : il occupe le reste de la fenêtre.
function useAvailableHeight(ref) {
  const [height, setHeight] = useState(MIN_HEIGHT);
  useEffect(() => {
    const measure = () => {
      if (!ref.current) { return; }
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(MIN_HEIGHT, Math.round(window.innerHeight - top - 24)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref]);
  return height;
}

// Chemin SVG d'une série : les valeurs absentes (null) coupent la ligne (pas d'interpolation trompeuse).
function seriesPath(points) {
  let path = "";
  let pen = false;
  for (const p of points) {
    if (p.y == null) { pen = false; continue; }
    path += `${pen ? "L" : "M"}${p.x},${p.y} `;
    pen = true;
  }
  return path.trim();
}

function CostChart({ sims }) {
  const box = useRef(null);
  const height = useAvailableHeight(box);
  const [hover, setHover] = useState(null);

  const values = sims.flatMap((s) => SERIES.map((serie) => serie.pick(s))).filter((v) => v != null);
  const innerW = Math.max(sims.length - 1, 1) * POINT_SPACING;
  const width = MARGIN.left + innerW + MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const yMax = (values.length ? Math.max(...values) : 0) * 1.05;
  const x = (i) => MARGIN.left + (sims.length > 1 ? (i * innerW) / (sims.length - 1) : innerW / 2);
  const y = (v) => MARGIN.top + innerH * (1 - v / yMax);

  const ticks = Array.from({ length: 6 }, (_, i) => Math.round((yMax * i) / 5));
  // Grille fine : le pas le plus serré qui garde des lignes espacées d'au moins GRID_MIN_GAP_PX.
  const gridStep = GRID_STEPS.find((step) => (innerH * step) / (yMax || 1) >= GRID_MIN_GAP_PX);
  const gridLines = [];
  if (gridStep) {
    for (let v = gridStep; v < yMax; v += gridStep) { gridLines.push(v); }
  }

  return (
    <div className="SimChart-scroll" ref={box}>
      {!values.length && <div className="Sim-empty">Aucun coût à tracer (simulations sans comparatif).</div>}
      {values.length > 0 && (
      <svg width={width} height={height} className="SimChart-svg" role="img"
           onMouseLeave={() => setHover(null)}>
        {/* Grille fine (€) puis graduations chiffrées */}
        {gridLines.map((v) => (
          <line key={`m${v}`} x1={MARGIN.left} x2={width - MARGIN.right} y1={y(v)} y2={y(v)} className="SimChart-grid-minor" />
        ))}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} className="SimChart-grid" />
            <text x={MARGIN.left - 8} y={y(t) + 4} textAnchor="end" className="SimChart-tick">{t.toLocaleString("fr-FR")}</text>
          </g>
        ))}
        {/* Axe X : jour simulé (+ pas si plusieurs sims le même jour) */}
        {sims.map((s, i) => (
          <text
            key={s.id}
            x={x(i)}
            y={height - MARGIN.bottom + 16}
            textAnchor="middle"
            className={`SimChart-tick${hover === i ? " on" : ""}`}
          >
            {fmtDay(s.day)}
            {s.duplicate && <tspan x={x(i)} dy={12}>pas {s.step_min}</tspan>}
          </text>
        ))}
        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={MARGIN.top} y2={MARGIN.top + innerH} className="SimChart-crosshair" />
        )}
        {/* Les 3 courbes et leurs points */}
        {SERIES.map((serie) => {
          const points = sims.map((s, i) => {
            const v = serie.pick(s);
            return { x: x(i), y: v == null ? null : y(v) };
          });
          return (
            <g key={serie.key}>
              <path d={seriesPath(points)} fill="none" stroke={serie.color} strokeWidth="2" />
              {sims.map((s, i) => {
                const v = serie.pick(s);
                if (v == null) { return null; }
                return (
                  <circle key={s.id} cx={x(i)} cy={y(v)} r={hover === i ? 5 : 3.5} fill={serie.color} />
                );
              })}
            </g>
          );
        })}
        {/* Zones de survol : une colonne par simulation, pour viser large */}
        {sims.map((s, i) => {
          const half = POINT_SPACING / 2;
          return (
            <rect
              key={`h${s.id}`}
              x={x(i) - half}
              y={MARGIN.top}
              width={POINT_SPACING}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          );
        })}
        {hover !== null && <Tooltip sim={sims[hover]} x={x(hover)} width={width} />}
      </svg>
      )}
    </div>
  );
}

// Encadré de survol : la journée pointée et le coût de chaque série, alignés.
function Tooltip({ sim, x, width }) {
  const flip = x + TIP.gap + TIP.w > width;
  const left = flip ? x - TIP.gap - TIP.w : x + TIP.gap;
  return (
    <g className="SimChart-tip" pointerEvents="none">
      <rect x={left} y={MARGIN.top + 6} width={TIP.w} height={TIP.h} rx="4" className="SimChart-tip-box" />
      <text x={left + 10} y={MARGIN.top + 22} className="SimChart-tip-title">
        {fmtDay(sim.day)} · pas {sim.step_min} min
      </text>
      {SERIES.map((serie, n) => (
        <g key={serie.key}>
          <rect x={left + 10} y={MARGIN.top + 31 + n * 14} width="8" height="8" rx="2" fill={serie.color} />
          <text x={left + 23} y={MARGIN.top + 38 + n * 14} className="SimChart-tip-text">{serie.label}</text>
          <text x={left + TIP.w - 10} y={MARGIN.top + 38 + n * 14} textAnchor="end" className="SimChart-tip-value">
            {fmtEur(serie.pick(sim))}
          </text>
        </g>
      ))}
    </g>
  );
}
Tooltip.propTypes = { sim: PropTypes.object, x: PropTypes.number, width: PropTypes.number };

CostChart.propTypes = { sims: PropTypes.array };

function SimulationChartPage({ centerId }) {
  const history = useHistory();
  const [sims, setSims] = useState(null);

  useEffect(() => {
    if (centerId) { api.listSimulations(centerId).then(setSims); }
  }, [centerId]);

  // Ordre chronologique du JOUR SIMULÉ (puis date de création pour les doublons de jour).
  const ordered = (sims || [])
    .slice()
    .sort((a, b) => (a.day === b.day ? a.created_at.localeCompare(b.created_at) : a.day.localeCompare(b.day)))
    .map((s, i, arr) => ({
      ...s,
      duplicate: (i > 0 && arr[i - 1].day === s.day) || (i + 1 < arr.length && arr[i + 1].day === s.day),
    }));

  return (
    <div className="Sim-page">
      <div className="Sim-controls">
        <button type="button" className="Sim-copy" onClick={() => history.push("/dispatch-simulation")}>
          ← Simulations
        </button>
        <h3 className="SimChart-title">Coûts des simulations — centre {centerId}</h3>
        <span className="SimChart-legend">
          {SERIES.map((serie) => (
            <span key={serie.key} className="SimChart-legend-item">
              <span className="SimChart-swatch" style={{ background: serie.color }} />
              {serie.label}
            </span>
          ))}
        </span>
      </div>
      {sims === null && <div className="Sim-empty">Chargement…</div>}
      {sims !== null && !ordered.length && (
        <div className="Sim-empty">Aucune simulation pour ce centre — crée-en une depuis la vue Simulations.</div>
      )}
      {ordered.length > 0 && <CostChart sims={ordered} />}
    </div>
  );
}
SimulationChartPage.propTypes = { centerId: PropTypes.number };

export default connect((state) => ({ centerId: extractCenterId(state.user) }))(SimulationChartPage);
